import { useState, forwardRef } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import * as Yup from 'yup'
import { Formik } from 'formik'
import OTPInput, { ResendOTP } from 'otp-input-react'

import {
  Button,
  TextField,
  Typography,
  Grid,
  Snackbar,
  Alert as MuiAlert,
  Container,
  Box,
  CardContent,
  Divider
} from '@mui/material'
import MainCard, {
  SnackBarHideDuration,
  SnackBarTransition
} from '../../utils/ui_utils'
import {
  useRegister,
  useSendAdminOtp,
  useVerifyAdminOtp
} from '../../modules/user/userHooks'
import { set } from 'lodash'

function SignUp() {
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)
  const [OTP, setOTP] = useState('')
  const [showOtpUI, setShowOtpUI] = useState(false)
  const { registerUser } = useRegister()
  const { userSendAdminOtp } = useSendAdminOtp()
  const { userVerifyAdminOtp } = useVerifyAdminOtp()
  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const toastMessage = (message, severity) => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
  })

  const dispatch = useDispatch()
  const history = useNavigate()

  const sendOTP = (mobile) => {
    let requestOTP = { mobile: mobile }
    userSendAdminOtp(requestOTP, (success, error, message) => {
      if (error) {
        toastMessage(message, 'error')
      } else {
        toastMessage('OTP Sent', 'success')
        setShowOtpUI(true)
      }
    })
  }

  return (
    <>
      <Helmet title="Sign Up" />
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <MainCard content={false}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography
                component="h1"
                variant="h4"
                align="center"
                gutterBottom
              >
                Sign Up
              </Typography>
              <Typography
                gutterBottom
                component="h2"
                variant="body1"
                align="center"
              >
                Create your account to get started
              </Typography>

              <Formik
                initialValues={{
                  first_name: '',
                  last_name: '',
                  user_name: '',
                  telephone: '',
                  mobile: '',
                  email: '',
                  password: '',
                  confirmPassword: '',
                  address: ''
                }}
                validationSchema={Yup.object().shape({
                  first_name: Yup.string()
                    .required('Required')
                    .min(2, 'Min length is 2 character'),
                  // last_name: Yup.string().required('Required'),
                  // email: Yup.string().email().required('Required'),
                  mobile: Yup.number()
                    .min(10, 'Min 10 digits')
                    .required('Required'),
                  user_name: Yup.string().required('Required'),
                  email: Yup.string().email().required('Required'),
                  password: Yup.string()
                    .min(6, 'Must be at least 6 characters')
                    .max(255)
                    .required('Required'),
                  confirmPassword: Yup.string().when('password', {
                    is: (val) => !!(val && val.length > 0),
                    then: Yup.string().oneOf(
                      [Yup.ref('password')],
                      'Passwords do not match'
                    )
                  }),
                  OTP: Yup.string().when('showOtpUI', {
                    is: (val) => val == true,
                    then: Yup.string().required('Required')
                  })
                })}
                onSubmit={async (
                  value,
                  { setErrors, setStatus, setSubmitting }
                ) => {
                  let values = _.cloneDeep(value)
                  if (!showOtpUI) {
                    sendOTP(values.mobile)
                  } else {
                    if (OTP.length < 4) return
                    let payload = {
                      mobile: values.mobile,
                      otp: OTP
                    }
                    userVerifyAdminOtp(
                      payload,
                      async (success, error, message) => {
                        if (error) {
                          toastMessage(message, 'error')
                          return
                        } else {
                          toastMessage('OTP Verified', 'success')
                          setShowOtpUI(false)
                          try {
                            if (values.telephone === '') {
                              delete values.telephone
                            }
                            delete values.confirmPassword
                            values.user_type = 'Owner'
                            try {
                              await registerUser(
                                values,
                                (success, error, message) => {
                                  if (error) {
                                    toastMessage(message, 'error')
                                  } else {
                                    toastMessage('User Registered', 'success')
                                    setTimeout(() => {
                                      history('/auth/sign-in')
                                    }, 1500)
                                  }
                                }
                              )
                            } catch (error) {
                              console.log('error', error)
                              toastMessage('Something went wrong', 'error')
                            }

                            setTimeout(() => {
                              history('/auth/sign-in')
                            }, 1500)
                          } catch (error) {
                            console.log('error', error)
                            // const message =
                            //   response.message || 'Something went wrong'
                            setStatus({ success: false })
                            setErrors({ submit: message })
                            setShowOtpUI(false)
                            setOTP('')
                            setSubmitting(false)
                          }
                        }
                      }
                    )
                  }
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  touched,
                  values
                }) => (
                  <form noValidate onSubmit={handleSubmit}>
                    {errors.submit && (
                      <Alert mt={2} mb={1} severity="warning">
                        {errors.submit}
                      </Alert>
                    )}

                    <Grid container spacing={1}>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="first_name"
                          label="First Name"
                          value={values.first_name}
                          margin="dense"
                          error={Boolean(
                            touched.first_name && errors.first_name
                          )}
                          fullWidth
                          helperText={touched.first_name && errors.first_name}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          disabled={showOtpUI}
                          // variant="outlined"
                          my={2}
                        />
                      </Grid>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="last_name"
                          label="Last Name"
                          value={values.last_name}
                          margin="dense"
                          error={Boolean(touched.last_name && errors.last_name)}
                          fullWidth
                          helperText={touched.last_name && errors.last_name}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          disabled={showOtpUI}
                          // variant="outlined"
                          my={2}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="mobile"
                          label="Mobile"
                          value={values.mobile}
                          margin="dense"
                          error={Boolean(touched.mobile && errors.mobile)}
                          fullWidth
                          helperText={touched.mobile && errors.mobile}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          disabled={showOtpUI}
                          inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                          }}
                          // variant="outlined"
                          my={2}
                        />
                      </Grid>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="email"
                          label="Email"
                          value={values.email}
                          margin="dense"
                          error={Boolean(touched.email && errors.email)}
                          fullWidth
                          helperText={touched.email && errors.email}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          type="email"
                          disabled={showOtpUI}
                          // variant="outlined"
                          my={2}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="user_name"
                          label="User ID"
                          value={values.user_name}
                          margin="dense"
                          error={Boolean(touched.user_name && errors.user_name)}
                          fullWidth
                          helperText={touched.user_name && errors.user_name}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          disabled={showOtpUI}
                          // variant="outlined"
                          my={2}
                        />
                      </Grid>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          name="telephone"
                          label="Telephone"
                          value={values.telephone}
                          margin="dense"
                          error={Boolean(touched.telephone && errors.telephone)}
                          fullWidth
                          helperText={touched.telephone && errors.telephone}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          disabled={showOtpUI}
                          // variant="outlined"
                          my={2}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          type="password"
                          name="password"
                          label="Password"
                          value={values.password}
                          margin="dense"
                          error={Boolean(touched.password && errors.password)}
                          fullWidth
                          helperText={touched.password && errors.password}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          disabled={showOtpUI}
                          my={3}
                        />
                      </Grid>
                      <Grid item md={6}>
                        <TextField
                          size="small"
                          type="password"
                          name="confirmPassword"
                          label="Confirm Password"
                          margin="dense"
                          value={values.confirmPassword}
                          error={Boolean(
                            touched.confirmPassword && errors.confirmPassword
                          )}
                          fullWidth
                          helperText={
                            touched.confirmPassword && errors.confirmPassword
                          }
                          onBlur={handleBlur}
                          onChange={handleChange}
                          disabled={showOtpUI}
                          my={3}
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={1}>
                      <Grid item md={12}>
                        <TextField
                          size="small"
                          type="address"
                          name="address"
                          label="Address"
                          margin="dense"
                          value={values.address}
                          error={Boolean(touched.address && errors.address)}
                          fullWidth
                          helperText={touched.address && errors.address}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          disabled={showOtpUI}
                          my={3}
                        />
                      </Grid>
                    </Grid>
                    {showOtpUI && (
                      <div sx={{ marginTop: 5 }}>
                        <Divider sx={{ marginTop: 5, marginBottom: 1 }} />
                        <Typography gutterBottom component="h2" variant="body1">
                          OTP Verification
                        </Typography>
                        {/* <Grid container sx={{ marginTop: 2 }} spacing={1} justifyContent="center" > */}
                        <OTPInput
                          value={OTP}
                          onChange={setOTP}
                          autoFocus
                          OTPLength={4}
                          otpType="number"
                          disabled={false}
                        />
                        <ResendOTP
                          onResendClick={() => sendOTP(values.mobile)}
                        />
                        {/* </Grid> */}
                      </div>
                    )}
                    <Grid
                      container
                      justifyContent="flex-end"
                      spacing={2}
                      style={{ paddingTop: 10 }}
                    >
                      {!showOtpUI && (
                        <Grid item>
                          <Button
                            fullWidth
                            type="submit"
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            Send OTP
                          </Button>
                        </Grid>
                      )}

                      {showOtpUI && (
                        <Grid item>
                          <Button
                            // type="submit"
                            fullWidth
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setOTP('')
                              setShowOtpUI(false)
                            }}
                          >
                            Back
                          </Button>
                        </Grid>
                      )}
                      {showOtpUI && (
                        <Grid item>
                          <Button
                            type="submit"
                            fullWidth
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            Verify and SignUp
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </form>
                )}
              </Formik>
              <div>
                <Snackbar
                  open={snackbar}
                  autoHideDuration={SnackBarHideDuration}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  onClose={handleSnackbarClose}
                  TransitionComponent={SnackBarTransition}
                >
                  <Alert onClose={handleSnackbarClose} severity={severity}>
                    {PopUpMessage}
                  </Alert>
                </Snackbar>
              </div>
            </CardContent>
          </MainCard>
        </Box>
      </Container>
    </>
  )
}

export default SignUp
